import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Search } from "lucide-react";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import PreFooterCTA from "../components/PreFooterCTA";
import { FAQ_CATEGORIES } from "../data/business";

const BREADCRUMBS = [{ label: "FAQs" }];

// Build stable FAQPage structured data from the full FAQ bank (not the filtered
// view) so the schema doesn't change as the user types in the search box.
function buildFaqSchema() {
  const mainEntity = [];
  FAQ_CATEGORIES.forEach((c) => {
    c.faqs.forEach((f) => {
      const text = `${f.a}${f.link ? f.link.label : ""}${f.aEnd || ""}`.trim();
      mainEntity.push({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text },
      });
    });
  });
  return { "@context": "https://schema.org", "@type": "FAQPage", mainEntity };
}

const FAQ_SCHEMA = buildFaqSchema();

export default function FAQ() {
  const [q, setQ] = useState("");
  const filtered = FAQ_CATEGORIES.map((c) => ({
    ...c,
    faqs: c.faqs.filter((f) => (f.q + f.a).toLowerCase().includes(q.toLowerCase())),
  })).filter((c) => c.faqs.length > 0);

  return (
    <Layout>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <section className="bg-[#1A1D21] text-white">
        <div className="abc-container py-12 md:py-16">
          <Breadcrumbs items={BREADCRUMBS} />
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold">Drain &amp; sewer <span className="text-[#E31D2B]">questions, answered.</span></h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">The most common questions homeowners ask us. Still stuck? Just call.</p>
          <div className="mt-6 max-w-xl relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search FAQs…" data-testid="faq-search"
              className="w-full rounded-xl pl-10 pr-4 py-3 bg-white text-[#1A1D21] outline-none focus:ring-2 focus:ring-[#54616E]" />
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 abc-bg-pale">
        <div className="abc-container grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-24 space-y-1">
              <div className="text-xs font-bold uppercase tracking-widest text-[#54616E] mb-2">Categories</div>
              {FAQ_CATEGORIES.map((c) => (
                <a key={c.name} href={`#${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="block px-3 py-2 rounded-lg text-sm text-[#1A1D21] hover:bg-[#EDEFF2]">
                  {c.name}
                </a>
              ))}
            </div>
          </aside>
          <div className="lg:col-span-9 space-y-10">
            {filtered.map((c) => (
              <div key={c.name} id={c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")} data-testid={`faq-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">{c.name}</h2>
                <div className="mt-4 space-y-3">
                  {c.faqs.map((f) => (
                    <details key={f.q} className="abc-card p-5 group">
                      <summary className="cursor-pointer list-none flex justify-between items-start gap-4">
                        <span className="font-display font-bold text-[#1A1D21]">{f.q}</span>
                        <ArrowRight className="w-4 h-4 text-[#54616E] flex-shrink-0 mt-1 group-open:rotate-90 transition" />
                      </summary>
                      <p className="mt-3 text-slate-600 leading-relaxed">
                        {f.a}
                        {f.link && (
                          <Link to={f.link.to} className="text-[#E31D2B] font-semibold underline underline-offset-2 hover:text-[#B71420]" data-testid="faq-inline-link">
                            {f.link.label}
                          </Link>
                        )}
                        {f.aEnd}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-slate-500" data-testid="faq-empty">No FAQs match your search. Try another keyword.</p>}
          </div>
        </div>
      </section>

      <PreFooterCTA />
    </Layout>
  );
}
